import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import InnerBox from '@repo/ui/inner-box'
import Loader from '@repo/ui/loader'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getOptions } from '../../../api/options/get-options'
import { getProducts } from '../../../api/products/get-products'
import { EndOfJourneyTemplateInput } from '../../../lib/types/models/end-of-journey-template/end-of-journey-template-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { createNumberArraySchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ValidationButtons from '../../elements/validation-buttons'

const schema: ZodType<EndOfJourneyTemplateInput> = z.object({
  name: createStringSchema('nom du modèle'),
  productIds: createNumberArraySchema('produits', { isInteger: true, minimumNumberOfItems: 1 }),
  optionIds: createNumberArraySchema('options', { isInteger: true, minimumNumberOfItems: 1 })
})

const EndOfStayTemplateForm = ({
  defaultValues,
  className,
  ...props
}: BaseFormProps<EndOfJourneyTemplateInput>) => {
  const form = useForm({
    mode: FORM_MODE,
    defaultValues,
    resolver: zodResolver(schema)
  })

  const { data: products, isPending: isProductsPending } = useQuery({
    queryKey: ['products'],
    queryFn: () => getProducts()
  })

  const { data: options, isPending: isOptionsPending } = useQuery({
    queryKey: ['options'],
    queryFn: () => getOptions()
  })

  if (isProductsPending || isOptionsPending) return <Loader center />

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(props.buttonsConfig))}>
        <InnerBox title="Enquête" className={cn(className, 'rounded-xl')}>
          <div className="flex flex-col gap-3">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledInput {...field} required type="text" label="Nom du template" />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="productIds"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledMultiSelectInput
                      {...field}
                      dropDownPosition="above"
                      label="Produit(s)"
                      variant="primary"
                      required
                      searchable
                      options={
                        products?.data.map((product) => ({
                          label: product.name,
                          value: product.id
                        })) ?? []
                      }
                      selectedOptions={
                        field.value?.map((productId) => ({
                          label:
                            products?.data.find((product) => product.id === productId)?.name ?? '',
                          value: productId
                        })) ?? []
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="optionIds"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledMultiSelectInput
                      {...field}
                      dropDownPosition="above"
                      label="Option(s)"
                      variant="primary"
                      required
                      searchable
                      options={
                        options?.data.map((option) => ({
                          label: getTranslatedName(option),
                          value: option.id
                        })) ?? []
                      }
                      selectedOptions={
                        field.value?.map((optionId) => ({
                          label: getTranslatedName(
                            options?.data.find((option) => option.id === optionId)
                          ),
                          value: optionId
                        })) ?? []
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </InnerBox>

        <ValidationButtons {...props} />
      </form>
    </Form>
  )
}

export default EndOfStayTemplateForm
